import { useEffect, useRef } from 'react';

/**
 * Saves right away when the tab is hidden or closed instead of waiting out a
 * debounce, retries as soon as the connection comes back, reports going
 * offline while anything is unsaved, and asks before closing while anything
 * is still unsaved.
 */
export function useFlushSaves(
  saveNow: () => Promise<void>,
  onOfflineWhileUnsaved: () => void,
  hasUnsaved: () => boolean,
) {
  const latest = useRef({ saveNow, onOfflineWhileUnsaved, hasUnsaved });
  latest.current = { saveNow, onOfflineWhileUnsaved, hasUnsaved };

  useEffect(() => {
    const save = () => void latest.current.saveNow();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') save();
    };
    const onOffline = () => {
      if (latest.current.hasUnsaved()) latest.current.onOfflineWhileUnsaved();
    };
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!latest.current.hasUnsaved()) return;
      save();
      event.preventDefault();
      event.returnValue = '';
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', save);
    window.addEventListener('online', save);
    window.addEventListener('offline', onOffline);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', save);
      window.removeEventListener('online', save);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, []);
}
